import { useMatches } from "react-router";

/**
 * The mobile app shell (docs/MOBILE_UI_PLAN.html D1–D3, D9): every route says in its `handle.shell` which tab it
 * belongs to and which chrome it wants. The deepest route wins per field.
 */
export const TABS = ["home", "catalog", "cart", "favorites", "cabinet"] as const;
export type TabKey = (typeof TABS)[number];

export interface Shell {
  /** The tab that lights up in the bottom bar (none for pages outside the tabs, e.g. legal pages). */
  tab?: TabKey;
  /** Bottom tab bar on phones; false on product, checkout and application pages (their own bar replaces it). */
  tabBar?: boolean;
  /** The home header (logo row + sticky search) on phones. */
  homeHeader?: boolean;
  /** The site footer on phones (always shown on desktop). */
  footer?: boolean;
  /** Grey canvas behind white blocks on phones (storefront pages); white otherwise. */
  canvas?: boolean;
}

/** Root route of each tab: where the tab leads first and where a second tap returns. */
export const TAB_ROOT: Record<TabKey, string> = {
  home: "/",
  catalog: "/catalog",
  cart: "/cart",
  favorites: "/favorites",
  cabinet: "/cabinet",
};

/** Merges the `handle.shell` of the matched routes, the deepest one winning per field. */
export function mergeShell(handles: readonly unknown[]): Shell {
  return handles.reduce<Shell>((shell, handle) => {
    if (typeof handle !== "object" || handle === null || !("shell" in handle)) return shell;
    const own = handle.shell;
    return typeof own === "object" && own !== null ? { ...shell, ...(own as Shell) } : shell;
  }, {});
}

export function useShell(): Shell {
  const matches = useMatches();
  return mergeShell(matches.map((match) => match.handle));
}

const MEMORY_PREFIX = "ishonch.tab.";
/** Tabs that remember where the customer was (a listing with filters, a cabinet section). */
const REMEMBERING: readonly TabKey[] = ["catalog", "cabinet"];

/** The last place inside a tab (D9: tapping the tab returns there); the tab root when nothing is remembered. */
export function tabTarget(tab: TabKey): string {
  if (!REMEMBERING.includes(tab)) return TAB_ROOT[tab];
  try {
    return sessionStorage.getItem(MEMORY_PREFIX + tab) ?? TAB_ROOT[tab];
  } catch {
    return TAB_ROOT[tab];
  }
}

/** Where a tab's memory may point: its own section (a product opened from the home page is not «the catalog»). */
const SECTION: Partial<Record<TabKey, readonly string[]>> = {
  catalog: ["/catalog", "/search"],
  cabinet: ["/cabinet"],
};

export function rememberTabLocation(tab: TabKey | undefined, url: string): void {
  if (!tab || !REMEMBERING.includes(tab)) return;
  if (!(SECTION[tab] ?? []).some((prefix) => url.startsWith(prefix))) return;
  try {
    sessionStorage.setItem(MEMORY_PREFIX + tab, url);
  } catch {
    // storage unavailable: the tab simply opens its root
  }
}
